import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import ImageUploadInput from '../components/ImageUploadInput';
import '../cuttingPhotos.css';
import {
  validateMediaFiles,
  uploadMedia,
  getMediaErrorMessage,
} from '../utils/imageUpload';
import { normalizeMediaList } from '../utils/media';

const PHOTO_STAGES = [
  'cut',
  'veg',
  'flowering',
  'harvest',
  'drying',
  'trim',
  'curing',
  'lab_tests',
  'issue',
];

const LANG_TO_LOCALE = {
  en: 'en-GB',
  ru: 'ru-RU',
  he: 'he-IL',
  es: 'es-ES',
  ua: 'uk-UA',
};

function toIsoString(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function toLocalInputValue(value) {
  const iso = toIsoString(value);
  if (!iso) return '';
  const date = new Date(iso);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function fromLocalInputValue(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function parsePosition(value) {
  if (value === undefined || value === null || value === '') return null;
  if (Number.isFinite(value)) return Number(value);
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatDateTimeDisplay(value, lang) {
  const iso = toIsoString(value);
  if (!iso) return '';
  const locale = LANG_TO_LOCALE[lang] || LANG_TO_LOCALE.en;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

function comparePhotosByTimeline(a, b) {
  const aDate = toIsoString(a.takenAt || a.date || a.createdAt) || toIsoString(a.createdAt) || toIsoString(a.date);
  const bDate = toIsoString(b.takenAt || b.date || b.createdAt) || toIsoString(b.createdAt) || toIsoString(b.date);
  const aTime = aDate ? new Date(aDate).getTime() : 0;
  const bTime = bDate ? new Date(bDate).getTime() : 0;
  if (aTime !== bTime) return aTime - bTime;
  const aPos = parsePosition(a.position ?? a.orderIndex);
  const bPos = parsePosition(b.position ?? b.orderIndex);
  const aPosVal = aPos === null ? Number.MAX_SAFE_INTEGER : aPos;
  const bPosVal = bPos === null ? Number.MAX_SAFE_INTEGER : bPos;
  if (aPosVal !== bPosVal) return aPosVal - bPosVal;
  const aId = a._id || '';
  const bId = b._id || '';
  return aId.localeCompare(bId);
}

function normalizePhoto(photo) {
  if (!photo) return photo;
  const takenAt = toIsoString(photo.takenAt || photo.date || photo.createdAt);
  const createdAt = toIsoString(photo.createdAt) || takenAt;
  const updatedAt = toIsoString(photo.updatedAt) || createdAt;
  const position = parsePosition(photo.position ?? photo.orderIndex);
  return {
    ...photo,
    _id: photo._id || photo.id || '',
    shortName: photo.shortName ?? photo.name ?? '',
    description: photo.description ?? '',
    stage: photo.stage ?? '',
    includeInDiary: Boolean(photo.includeInDiary),
    takenAt,
    createdAt,
    updatedAt,
    position,
    orderIndex: position,
  };
}

export default function CuttingDetailPage() {
  const { id } = useParams();
  const { t, lang: currentLang, translations: translationsMap } = useLang();
  const [cutting, setCutting] = useState(null);
  const [diaryForm, setDiaryForm] = useState({
    note: '',
    images: [],
    imagePublicIds: [],
  });
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoShortName, setPhotoShortName] = useState('');
  const [photoDescriptionDetailed, setPhotoDescriptionDetailed] = useState('');
  const [photoStage, setPhotoStage] = useState('');
  const [photoIncludeInDiary, setPhotoIncludeInDiary] = useState(false);
  const [photoTakenAt, setPhotoTakenAt] = useState(() => toLocalInputValue(new Date()));
  const [photoPosition, setPhotoPosition] = useState('');
  const [photoMsg, setPhotoMsg] = useState(false);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [stageFilter, setStageFilter] = useState('');
  const [diaryOnly, setDiaryOnly] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [editPhoto, setEditPhoto] = useState(null);
  const [photoPendingDeletion, setPhotoPendingDeletion] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [movingPhotoId, setMovingPhotoId] = useState(null);
  const stageLabels = useMemo(() => {
    const fallback = translationsMap?.en?.photoStageLabels || {};
    const current = translationsMap?.[currentLang]?.photoStageLabels;
    return current ? { ...fallback, ...current } : fallback;
  }, [translationsMap, currentLang]);
  const isRtl = currentLang === 'he';
  const photos = cutting?.photos || [];
  const filteredPhotos = useMemo(
    () =>
      photos.filter((photo) => {
        if (diaryOnly && !photo.includeInDiary) return false;
        if (stageFilter && photo.stage !== stageFilter) return false;
        return true;
      }),
    [photos, diaryOnly, stageFilter]
  );
  const diaryPhotosCount = useMemo(
    () => photos.filter((photo) => photo.includeInDiary).length,
    [photos]
  );
  const stageOptions = useMemo(
    () => PHOTO_STAGES.map((stage) => ({ value: stage, label: stageLabels[stage] || stage })),
    [stageLabels]
  );
  const token = localStorage.getItem('token');
  const loggedIn = Boolean(token);
  if (!token) return <Navigate to="/login" replace />;
  const decodeRole = (tok) => {
    if (!tok) return null;
    try {
      return JSON.parse(atob(tok.split('.')[1])).role;
    } catch {
      return null;
    }
  };
  const role = decodeRole(token);
  const canEdit = role === 'admin' || role === 'superuser';

  const fetchCutting = () => {
    const opts = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : undefined;
    fetch(`/api/cuttings/${id}`, opts)
      .then((r) => r.json())
      .then((data) => {
        const normalizedPhotos = Array.isArray(data.photos)
          ? data.photos.map(normalizePhoto).sort(comparePhotosByTimeline)
          : [];
        setCutting({ ...data, photos: normalizedPhotos });
      })
      .catch(() => setCutting(null));
  };

  useEffect(() => {
    fetchCutting();
  }, [id]);

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!token || files.length === 0) return;
    const err = validateMediaFiles(files);
    if (err) {
      alert(getMediaErrorMessage(err, t));
      e.target.value = '';
      return;
    }
    setUploading(true);
    for (const f of files) {
      try {
        const d = await uploadMedia(f, token);
        setDiaryForm((form) => ({
          ...form,
          images: [...form.images, {
            url: d.url,
            publicId: d.publicId,
            type: d.type,
            thumbnailUrl: d.thumbnailUrl,
          }],
          imagePublicIds: [...form.imagePublicIds, d.publicId],
        }));
      } catch (e2) {
        alert(getMediaErrorMessage(e2.message, t));
        break;
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const resetForm = () => {
    setDiaryForm({ note: '', images: [], imagePublicIds: [] });
    setEditId(null);
  };

  const saveEntry = async (e) => {
    e.preventDefault();
    const body = JSON.stringify(diaryForm);
    const method = editId ? 'PUT' : 'POST';
    const url = editId
      ? `/api/admin/cuttings/${cutting._id}/diary/${editId}`
      : `/api/admin/cuttings/${cutting._id}/diary`;
    await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
    });
    resetForm();
    fetchCutting();
  };

  const editEntry = (entry) => {
    setEditId(entry._id);
    setDiaryForm({
      note: entry.note || '',
      images: normalizeMediaList(entry.images || []),
      imagePublicIds: entry.imagePublicIds || [],
    });
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    await fetch(`/api/admin/cuttings/${cutting._id}/diary/${entryId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchCutting();
  };

  const deleteDiaryPhoto = async (entryId, publicId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    await fetch(
      `/api/admin/cuttings/${cutting._id}/diary/${entryId}/images/${publicId}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setCutting((c) => {
      const updated = { ...c };
      const entry = updated.diary.find((e) => e._id === entryId);
      if (entry) {
        const ids = Array.isArray(entry.imagePublicIds)
          ? entry.imagePublicIds
          : [];
        const idx = ids.indexOf(publicId);
        if (idx !== -1) ids.splice(idx, 1);
        entry.imagePublicIds = ids;
        if (Array.isArray(entry.images)) {
          entry.images = entry.images.filter((img, index) => {
            if (typeof img === 'string') return index !== idx;
            return img?.publicId !== publicId;
          });
        }
      }
      return { ...updated };
    });
  };

  const requestDeletePhoto = (photo) => {
    setPhotoPendingDeletion(photo);
  };

  const confirmDeletePhoto = async () => {
    const pending = photoPendingDeletion;
    setPhotoPendingDeletion(null);
    if (!pending) return;
    const res = await fetch(`/api/cuttings/${cutting._id}/photos/${pending._id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setCutting((c) => {
        if (!c) return c;
        const photos = (c.photos || []).filter((p) => p._id !== pending._id);
        return { ...c, photos };
      });
      setActionMsg({ type: 'success', text: t('commandSuccess') });
    } else {
      setActionMsg({ type: 'error', text: t('commandError') });
    }
    setTimeout(() => setActionMsg(null), 3000);
  };

  const updatePhotoMetadata = async (photoId, payload, successMessage) => {
    const res = await fetch(`/api/cuttings/${cutting._id}/photos/${photoId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      setActionMsg({ type: 'error', text: t('commandError') });
      setTimeout(() => setActionMsg(null), 3000);
      return null;
    }
    const updatedPhoto = normalizePhoto(await res.json());
    setCutting((c) => {
      if (!c) return c;
      const photos = (c.photos || []).map((p) =>
        p._id === updatedPhoto._id ? { ...p, ...updatedPhoto } : p
      );
      photos.sort(comparePhotosByTimeline);
      return { ...c, photos };
    });
    if (successMessage) {
      setActionMsg({ type: 'success', text: successMessage });
      setTimeout(() => setActionMsg(null), 3000);
    }
    return updatedPhoto;
  };

  const movePhotoInTimeline = async (photoId, direction) => {
    if (!photoId || !direction) return;
    setMovingPhotoId(photoId);
    try {
      const res = await fetch(`/api/cuttings/${cutting._id}/photos/${photoId}/move`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ direction }),
      });
      if (!res.ok) {
        setActionMsg({ type: 'error', text: t('commandError') });
        return;
      }
      const updated = (await res.json())
        .map(normalizePhoto)
        .sort(comparePhotosByTimeline);
      setCutting((c) => (c ? { ...c, photos: updated } : c));
      setActionMsg({ type: 'success', text: t('commandSuccess') });
    } catch (err) {
      console.error('Failed to move photo in timeline', err);
      setActionMsg({ type: 'error', text: t('commandError') });
    } finally {
      setMovingPhotoId(null);
      setTimeout(() => setActionMsg(null), 3000);
    }
  };

  const toggleDiaryPhoto = async (photo, included) => {
    await updatePhotoMetadata(photo._id, { includeInDiary: included }, null);
  };

  const downloadDiary = async () => {
    if (!cutting) return;
    setExporting(true);
    try {
      const res = await fetch(
        `/api/cuttings/${cutting._id}/diary.pdf?lang=${currentLang}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        let details = {};
        try {
          details = await res.json();
        } catch {
          details = {};
        }
        if (res.status === 400 && details.error === 'too_many_photos') {
          setActionMsg({
            type: 'error',
            text: t('diaryExportTooMany', details.limit || 150),
          });
        } else if (res.status === 400 && details.error === 'no_photos') {
          setActionMsg({ type: 'error', text: t('diaryExportNoPhotos') });
        } else {
          setActionMsg({ type: 'error', text: t('commandError') });
        }
        setTimeout(() => setActionMsg(null), 3000);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `garden-diary-${cutting.code || cutting._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Diary export failed', err);
      setActionMsg({ type: 'error', text: t('commandError') });
      setTimeout(() => setActionMsg(null), 3000);
    } finally {
      setExporting(false);
    }
  };

  const startEditPhoto = (photo) => {
    if (!photo) return;
    setEditPhoto({
      _id: photo._id,
      shortName: photo.shortName || '',
      description: photo.description || '',
      stage: photo.stage || '',
      includeInDiary: Boolean(photo.includeInDiary),
      takenAt: toLocalInputValue(photo.takenAt || photo.date || photo.createdAt),
      position: photo.position ?? photo.orderIndex ?? '',
    });
  };

  const savePhotoEdit = async () => {
    if (!editPhoto) return;
    const payload = {
      shortName: editPhoto.shortName,
      description: editPhoto.description,
      stage: editPhoto.stage || null,
      includeInDiary: Boolean(editPhoto.includeInDiary),
      takenAt: fromLocalInputValue(editPhoto.takenAt),
      position:
        editPhoto.position === '' || editPhoto.position === null
          ? null
          : editPhoto.position,
    };
    const updated = await updatePhotoMetadata(
      editPhoto._id,
      payload,
      t('commandSuccess')
    );
    if (updated) {
      setEditPhoto(null);
    }
  };

  const uploadPhoto = async (e) => {
    e.preventDefault();
    if (!photoFile || !photoShortName) return;
    const data = new FormData();
    data.append('file', photoFile);
    data.append('shortName', photoShortName);
    data.append('description', photoDescriptionDetailed);
    if (photoStage) data.append('stage', photoStage);
    data.append('includeInDiary', photoIncludeInDiary ? 'true' : 'false');
    const takenAtIso = fromLocalInputValue(photoTakenAt);
    if (takenAtIso) data.append('takenAt', takenAtIso);
    if (photoPosition !== '') data.append('position', photoPosition);
    const res = await fetch(`/api/cuttings/${cutting._id}/photo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: data,
    });
    if (res.ok) {
      const saved = normalizePhoto(await res.json());
      setCutting((c) => {
        if (!c) return c;
        const photos = [...(c.photos || []), saved];
        photos.sort(comparePhotosByTimeline);
        return { ...c, photos };
      });
      setPhotoMsg(true);
    } else {
      setActionMsg({ type: 'error', text: t('commandError') });
      setTimeout(() => setActionMsg(null), 3000);
    }
    setPhotoFile(null);
    setPhotoShortName('');
    setPhotoDescriptionDetailed('');
    setPhotoStage('');
    setPhotoIncludeInDiary(false);
    setPhotoTakenAt(toLocalInputValue(new Date()));
    setPhotoPosition('');
    setPhotoInputKey((k) => k + 1);
    setTimeout(() => setPhotoMsg(false), 3000);
  };

  if (!cutting) {
    return (
      <div className="container mt-3">
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mt-3">
      <h1 className="mb-3">{cutting.code}</h1>
      {actionMsg && (
        <div
          className={`alert alert-${
            actionMsg.type === 'success' ? 'success' : 'danger'
          }`}
          role="alert"
        >
          {actionMsg.text}
        </div>
      )}
      {cutting.strainName && <p>{cutting.strainName}</p>}
      {cutting.status && <p>{cutting.status}</p>}
      {cutting.location && <p>{cutting.location}</p>}
      <div className="mb-3">
        {Array.isArray(cutting.images) && cutting.images.length > 0 ? (
          <div className="d-flex flex-wrap">
            {normalizeMediaList(cutting.images)
              .slice()
              .sort((a, b) => Number(b.isMain) - Number(a.isMain))
              .map((img) => (
                <div
                  className="card me-2 mb-2"
                  style={{ width: '200px' }}
                  key={img.publicId || img.url}
                >
                  <div className="cutting-photo-wrapper">
                    {img.type === 'video' ? (
                      <video
                        src={img.url}
                        poster={img.thumbnailUrl || img.url}
                        controls
                        className="card-img-top cutting-photo"
                      />
                    ) : (
                      <img
                        src={img.url}
                        alt=""
                        className="card-img-top cutting-photo"
                      />
                    )}
                  </div>
                  <div className="card-body p-2">
                    <p className="card-text text-center small text-muted">
                      {img.name || 'No title'}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          cutting.photo && (
            <img
              src={cutting.photo}
              alt=""
              loading="lazy"
              className="img-fluid me-2 mb-2"
              style={{ width: 200, height: 'auto' }}
            />
          )
        )}
      </div>

      <div className="d-flex flex-wrap align-items-end gap-3 mb-3" dir={isRtl ? 'rtl' : 'ltr'}>
        <div style={{ minWidth: '200px' }}>
          <label className="form-label">{t('photoStageFilter')}</label>
          <select
            className="form-select"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
          >
            <option value="">{t('photoStageAll')}</option>
            {stageOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="diaryOnlyToggle"
            checked={diaryOnly}
            onChange={(e) => setDiaryOnly(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="diaryOnlyToggle">
            {t('photoDiaryOnly')}
          </label>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={downloadDiary}
            disabled={exporting || diaryPhotosCount === 0}
          >
            {exporting ? t('loading') : t('downloadDiary')}
            {diaryPhotosCount > 0 ? ` (${diaryPhotosCount})` : ''}
          </button>
        </div>
      </div>

      <div className="table-responsive mb-4" dir={isRtl ? 'rtl' : 'ltr'}>
        {filteredPhotos.length > 0 ? (
          <table className={`table table-sm align-middle ${isRtl ? 'text-end' : ''}`}>
            <thead>
              <tr>
                <th scope="col" style={{ width: '90px' }}>{t('photo')}</th>
                <th scope="col">{t('photoShortName')}</th>
                <th scope="col">{t('photoStage')}</th>
                <th scope="col">{t('photoTakenAt')}</th>
                <th scope="col" className="text-center">{t('photoMove')}</th>
                <th scope="col" className="text-center">{t('photoIncludeInDiary')}</th>
                <th scope="col" className="text-nowrap" />
              </tr>
            </thead>
            <tbody>
              {filteredPhotos.map((photo) => {
                const previewUrl =
                  photo.type === 'video'
                    ? photo.thumbnailUrl || photo.url
                    : photo.url;
                const stageLabel =
                  photo.stage ? stageLabels[photo.stage] || photo.stage : '—';
                const takenAtLabel =
                  formatDateTimeDisplay(photo.takenAt || photo.createdAt, currentLang) ||
                  '—';
                const indexInAll = photos.findIndex((p) => p._id === photo._id);
                const canMoveLeft = indexInAll > 0;
                const canMoveRight = indexInAll >= 0 && indexInAll < photos.length - 1;
                const isMoving = movingPhotoId === photo._id;
                return (
                  <tr key={photo._id || photo.publicId || photo.url}>
                    <td>
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt={photo.shortName || 'preview'}
                          className="rounded"
                          style={{
                            width: '72px',
                            height: '72px',
                            objectFit: 'cover',
                            cursor: 'pointer',
                          }}
                          onClick={() => setViewPhoto(photo)}
                        />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {photo.shortName || (
                        <span className="text-muted">{t('photoShortName')}</span>
                      )}
                    </td>
                    <td>{stageLabel}</td>
                    <td>{takenAtLabel}</td>
                    <td className="text-center">
                      {canEdit ? (
                        <div className="btn-group" role="group" aria-label={t('photoMove')}>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            title={t('moveLeft')}
                            onClick={() => movePhotoInTimeline(photo._id, 'left')}
                            disabled={isMoving || !canMoveLeft}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            title={t('moveRight')}
                            onClick={() => movePhotoInTimeline(photo._id, 'right')}
                            disabled={isMoving || !canMoveRight}
                          >
                            →
                          </button>
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={photo.includeInDiary}
                        disabled={!canEdit}
                        onChange={(e) => toggleDiaryPhoto(photo, e.target.checked)}
                      />
                    </td>
                    <td className="text-nowrap">
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={() => setViewPhoto(photo)}
                      >
                        {t('details')}
                      </button>
                      {canEdit && (
                        <>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-warning"
                            onClick={() => startEditPhoto(photo)}
                          >
                            {t('edit')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger"
                            onClick={() => requestDeletePhoto(photo)}
                          >
                            {t('delete')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="text-muted">{t('noData')}</div>
        )}
      </div>

      {canEdit && (
        <form onSubmit={uploadPhoto} className="mb-4" dir={isRtl ? 'rtl' : 'ltr'}>
          {photoMsg && (
            <div className="alert alert-success" role="alert">
              {t('photoSaved')}
            </div>
          )}
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label">{t('photo')}</label>
              <ImageUploadInput
                key={photoInputKey}
                className="form-control"
                onChange={(e) => setPhotoFile(e.target.files[0])}
                required
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">{t('photoShortName')}</label>
              <input
                type="text"
                className="form-control"
                value={photoShortName}
                onChange={(e) => setPhotoShortName(e.target.value)}
                required
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">{t('photoTakenAt')}</label>
              <input
                type="datetime-local"
                className="form-control"
                value={photoTakenAt}
                onChange={(e) => setPhotoTakenAt(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">{t('photoStage')}</label>
              <select
                className="form-select"
                value={photoStage}
                onChange={(e) => setPhotoStage(e.target.value)}
              >
                <option value="">{t('photoStageAll')}</option>
                {stageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row g-3 align-items-end mt-2">
            <div className="col-12 col-md-6">
              <label className="form-label">{t('photoDescriptionDetailed')}</label>
              <textarea
                className="form-control"
                value={photoDescriptionDetailed}
                onChange={(e) => setPhotoDescriptionDetailed(e.target.value)}
                rows={3}
              />
            </div>
            <div className="col-6 col-md-2">
              <label className="form-label">{t('photoPosition')}</label>
              <input
                type="number"
                className="form-control"
                value={photoPosition}
                onChange={(e) => setPhotoPosition(e.target.value)}
              />
            </div>
            <div className="col-6 col-md-2 d-flex align-items-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="includeInDiaryNew"
                  checked={photoIncludeInDiary}
                  onChange={(e) => setPhotoIncludeInDiary(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="includeInDiaryNew">
                  {t('photoIncludeInDiary')}
                </label>
              </div>
            </div>
            <div className="col-12 col-md-2 d-grid">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={!photoFile || !photoShortName}
              >
                {t('savePhoto')}
              </button>
            </div>
          </div>
        </form>
      )}

      {loggedIn && (
        <div className="mt-4">
          <h3>{t('gardenerDiary')}</h3>
          {cutting.diary && cutting.diary.length > 0 ? (
            <ul className="list-unstyled">
              {cutting.diary
                .slice()
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                .map((d) => (
                  <li key={d._id} className="mb-3">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex justify-content-between">
                          <small className="text-muted">
                            {formatDateTimeDisplay(d.timestamp, currentLang)}
                            {d.authorName ? ` • ${d.authorName}` : ''}
                          </small>
                          {canEdit && (
                            <span>
                              <button
                                className="btn btn-sm btn-outline-primary me-1"
                                onClick={() => editEntry(d)}
                              >
                                {t('edit')}
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteEntry(d._id)}
                              >
                                {t('delete')}
                              </button>
                            </span>
                          )}
                        </div>
                        {d.note && <p className="mt-2 mb-0">{d.note}</p>}
                        {d.images && d.images.length > 0 && (
                          <div className="row mt-2">
                            {normalizeMediaList(d.images || []).map((img, idx) => {
                              const url = img.url;
                              const label = img.name || img.label;
                              const removableId =
                                (Array.isArray(d.imagePublicIds) &&
                                  d.imagePublicIds[idx]) ||
                                img.publicId;
                              return (
                                <div
                                  key={img.publicId || url || idx}
                                  className="col-12 col-sm-6 col-md-4 col-lg-3"
                                >
                                  <div className="card mb-3 h-100">
                                    {img.type === 'video' ? (
                                      <video
                                        src={url}
                                        poster={img.thumbnailUrl || url}
                                        controls
                                        className="card-img-top img-fluid cutting-photo"
                                      />
                                    ) : (
                                      <img
                                        src={url}
                                        alt="cutting photo"
                                        className="card-img-top img-fluid cutting-photo"
                                      />
                                    )}
                                    <div className="card-body p-2">
                                      {label && (
                                        <p className="card-text text-center mb-1">
                                          {label}
                                        </p>
                                      )}
                                      {canEdit &&
                                        removableId && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-danger w-100 mt-2"
                                            onClick={() =>
                                              deleteDiaryPhoto(d._id, removableId)
                                            }
                                          >
                                            {t('delete')}
                                          </button>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          ) : (
            <p>{t('noData')}</p>
          )}

          {canEdit && (
            <form onSubmit={saveEntry} className="mt-3">
              <textarea
                className="form-control mb-2"
                placeholder={t('comment')}
                value={diaryForm.note}
                onChange={(e) =>
                  setDiaryForm({ ...diaryForm, note: e.target.value })
                }
              />
              <ImageUploadInput
                multiple
                className="form-control mb-2"
                onChange={handleFile}
              />
              {uploading && <div className="mb-2">{t('photoUploading')}</div>}
              {diaryForm.images.length > 0 && (
                <div className="mb-2">
                  {diaryForm.images.map((p) => {
                    const url = p?.url || p;
                    const key = p?.publicId || url;
                    if (!url) return null;
                    return p?.type === 'video' ? (
                      <video
                        key={key}
                        src={url}
                        poster={p.thumbnailUrl || url}
                        controls
                        className="img-fluid me-2 mb-2"
                        style={{ width: 120, height: 120, objectFit: 'cover' }}
                      />
                    ) : (
                      <img
                        key={key}
                        src={url}
                        alt=""
                        loading="lazy"
                        className="img-fluid me-2 mb-2"
                        style={{ width: 120, height: 120, objectFit: 'cover' }}
                      />
                    );
                  })}
                </div>
              )}
              <button className="btn btn-primary" type="submit">
                {editId ? t('save') : t('addLog')}
              </button>
              {editId && (
                <button
                  type="button"
                  className="btn btn-secondary ms-2"
                  onClick={resetForm}
                >
                  {t('cancel')}
                </button>
              )}
            </form>
          )}
        </div>
      )}
      {viewPhoto && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          onClick={() => setViewPhoto(null)}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" dir={isRtl ? 'rtl' : 'ltr'}>
              <div className="modal-header">
                <h5 className="modal-title">{t('photoDetails')}</h5>
                <button type="button" className="btn-close" onClick={() => setViewPhoto(null)}></button>
              </div>
              <div className="modal-body">
                {viewPhoto.type === 'video' ? (
                  <video
                    src={viewPhoto.url}
                    poster={viewPhoto.thumbnailUrl || viewPhoto.url}
                    controls
                    className="img-fluid mb-3"
                  />
                ) : (
                  <img
                    src={viewPhoto.url}
                    alt={viewPhoto.shortName || 'preview'}
                    className="img-fluid mb-3"
                  />
                )}
                <p className="fw-bold mb-1">{viewPhoto.shortName || t('photoShortName')}</p>
                <p className="mb-1">
                  {t('photoStage')}: {viewPhoto.stage ? stageLabels[viewPhoto.stage] || viewPhoto.stage : t('photoStageAll')}
                </p>
                <p className="mb-1">
                  {t('photoTakenAt')}: {formatDateTimeDisplay(viewPhoto.takenAt || viewPhoto.createdAt, currentLang) || '—'}
                </p>
                {(viewPhoto.position ?? '') !== '' && (
                  <p className="mb-1">
                    {t('photoPosition')}: {viewPhoto.position}
                  </p>
                )}
                <p className="mb-1">
                  {t('photoIncludeInDiary')}: {viewPhoto.includeInDiary ? '✓' : '—'}
                </p>
                {viewPhoto.description && <p className="mt-3 mb-0">{viewPhoto.description}</p>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setViewPhoto(null)}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {editPhoto && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          onClick={() => setEditPhoto(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" dir={isRtl ? 'rtl' : 'ltr'}>
              <div className="modal-header">
                <h5 className="modal-title">{t('edit')}</h5>
                <button type="button" className="btn-close" onClick={() => setEditPhoto(null)}></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-2"
                  value={editPhoto.shortName}
                  onChange={(e) => setEditPhoto((prev) => ({ ...prev, shortName: e.target.value }))}
                  placeholder={t('photoShortName')}
                />
                <textarea
                  className="form-control mb-2"
                  value={editPhoto.description}
                  onChange={(e) => setEditPhoto((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder={t('photoDescriptionDetailed')}
                  rows={3}
                />
                <select
                  className="form-select mb-2"
                  value={editPhoto.stage}
                  onChange={(e) => setEditPhoto((prev) => ({ ...prev, stage: e.target.value }))}
                >
                  <option value="">{t('photoStageAll')}</option>
                  {stageOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  type="datetime-local"
                  className="form-control mb-2"
                  value={editPhoto.takenAt || ''}
                  onChange={(e) => setEditPhoto((prev) => ({ ...prev, takenAt: e.target.value }))}
                />
                <input
                  type="number"
                  className="form-control mb-2"
                  value={editPhoto.position ?? ''}
                  onChange={(e) => setEditPhoto((prev) => ({ ...prev, position: e.target.value }))}
                  placeholder={t('photoPosition')}
                />
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="editIncludeInDiary"
                    checked={Boolean(editPhoto.includeInDiary)}
                    onChange={(e) => setEditPhoto((prev) => ({ ...prev, includeInDiary: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="editIncludeInDiary">
                    {t('photoIncludeInDiary')}
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditPhoto(null)}>
                  {t('cancel')}
                </button>
                <button type="button" className="btn btn-primary" onClick={savePhotoEdit}>
                  {t('save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {photoPendingDeletion && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          onClick={() => setPhotoPendingDeletion(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" dir={isRtl ? 'rtl' : 'ltr'}>
              <div className="modal-header">
                <h5 className="modal-title">{t('delete')}</h5>
                <button type="button" className="btn-close" onClick={() => setPhotoPendingDeletion(null)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">{t('confirmDelete')}</p>
                <p className="mb-0 text-muted">{photoPendingDeletion.shortName || photoPendingDeletion.description || photoPendingDeletion.url}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPhotoPendingDeletion(null)}>
                  {t('cancel')}
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDeletePhoto}>
                  {t('delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
